import type { Conversations } from '@/components/Chat/types';
import InsightConversationStore, { decodeMessages } from '@/services/insightConversation';
import type { InsightConversationRow } from '@/services/insightConversation';
import { getReduxState } from '@/utils/reduxSaga';
import { logger } from '@/utils/logger';
import type { BedrockMessageContent } from '@/utils/beemAgentTypes';
import type { InsightTraces } from '@/models/insight';

// Minimal creator/owner descriptor stored on the record (the backend swap resolves a full user).
export type Creator = { id: string; name: string };

export type Conversation = {
  id: string;
  title: string;
  messages: Conversations; // BedrockMessage[]
  updatedAt: number;
  // Who created the conversation (shown as an avatar in the list).
  createdBy?: Creator;
  // Whether this conversation has been shared with the rest of the workspace. Stored with the
  // conversation; letting other members read shared ones is still to come (owner-only for now).
  shared?: boolean;
  // The project this conversation belongs to (undefined = loose / no project).
  projectId?: string;
  // The page the conversation was started from, when summoned via the AI panel on a detail page
  // (e.g. "dashboard:<id>", "dataset:<id>"). Lets the panel resume the conversation for the
  // specific page it's reopened on. Undefined for conversations started on the standalone page.
  originKey?: string;
  // The agent memory session this conversation's turns are sent with. Set on the first turn;
  // older records may lack it until their next turn.
  sessionId?: string;
  // Per-answer reasoning summaries and memory use, keyed by the answer's index in `messages`.
  traces?: InsightTraces;
  // Someone else's, shared with the workspace: shown, never written. Continuing it saves the
  // continuation as the reader's own conversation.
  readOnly?: boolean;
};

export interface ConversationsRepo {
  list(): Promise<Conversation[]>;
  get(id: string): Promise<Conversation | undefined>;
  create(
    title: string,
    messages: Conversations,
    projectId?: string,
    createdBy?: Creator,
    originKey?: string,
    sessionId?: string,
  ): Promise<Conversation>;
  rename(id: string, title: string): Promise<void>;
  remove(id: string): Promise<void>;
  // Shares/unshares the whole conversation with the rest of the workspace (the flag, for now).
  share(id: string): Promise<void>;
  unshare(id: string): Promise<void>;
  // Conversations belonging to a project (newest updatedAt first).
  listByProject(projectId: string): Promise<Conversation[]>;
  // Conversations started from a given page/origin (newest updatedAt first). Used by the AI panel
  // to resume the conversation for the specific detail page it's reopened on.
  listByOrigin(originKey: string): Promise<Conversation[]>;
  // Assigns (or clears, with null) a conversation's project. Not a content change, so updatedAt
  // is left untouched.
  setProject(id: string, projectId: string | null): Promise<void>;
  // Creates-or-replaces a conversation by `id`. Used to persist an in-progress conversation as
  // its message list grows (without minting a new id on every turn). `updatedAt` advances ONLY
  // when the message content changes — so resuming a conversation doesn't reshuffle history.
  upsert(conversation: Conversation): Promise<Conversation>;
}

// Derives a short title from a conversation's first user message, for records created without an
// explicit title (e.g. the auto-persisted "current" conversation).
const firstQuestion = (messages: Conversations): string => {
  const firstUserMessage = messages.find((message) => message.role === 'user');
  return (
    firstUserMessage?.content.find((item: BedrockMessageContent) => item.text)?.text?.trim() ?? ''
  );
};

// The whole first question: every place that shows a title truncates it to fit with CSS. The cap
// only keeps a pasted wall of text out of the record.
export const deriveConversationTitle = (messages: Conversations): string => {
  const MAX_LENGTH = 300;
  const text = firstQuestion(messages);
  if (!text) return 'Untitled conversation';
  return text.length > MAX_LENGTH ? `${text.slice(0, MAX_LENGTH)}…` : text;
};

// The untruncated title. Titles used to be derived at 60 characters, and records saved then still
// carry the cut; a title the user set is kept as they wrote it.
export const fullConversationTitle = (messages: Conversations, title: string): string => {
  const question = firstQuestion(messages);
  return title.endsWith('…') && question.startsWith(title.slice(0, -1)) ? question : title;
};

const uid = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? `c_${crypto.randomUUID()}`
    : `c_${Date.now()}_${Math.floor(Math.random() * 1e9)}`;

// Whose conversations, in which workspace: the signed-in user and the open workspace.
export type RepoContext = { userId: string; workspaceId: string; userName?: string };
export const repoContext = (): RepoContext | null => {
  const state = getReduxState();
  const user = state.user.currentUser;
  const workspaceId = state.organization.currentWorkspace;
  return user && workspaceId ? { userId: user.id, workspaceId, userName: user.name } : null;
};

const parseTraces = (raw: string | null | undefined): InsightTraces | undefined => {
  if (!raw) return undefined;
  try {
    return typeof raw === 'string' ? JSON.parse(raw) : (raw as InsightTraces);
  } catch {
    return undefined;
  }
};

const fromRow = (row: InsightConversationRow): Conversation => {
  const messages = decodeMessages(row.messages);
  return {
    id: row.id,
    title: fullConversationTitle(messages, row.title),
    messages,
    updatedAt: Date.parse(row.lastActivityAt) || Date.now(),
    createdBy: { id: row.userId, name: row.createdByName ?? '' },
    shared: !!row.shared,
    projectId: row.projectId ?? undefined,
    originKey: row.originKey ?? undefined,
    sessionId: row.sessionId ?? undefined,
    traces: parseTraces(row.traces),
  };
};

const fieldsOf = (c: Conversation) => ({
  title: c.title,
  messages: c.messages,
  traces: c.traces ?? {},
  projectId: c.projectId ?? null,
  originKey: c.originKey ?? null,
  sessionId: c.sessionId ?? null,
  shared: !!c.shared,
  createdByName: c.createdBy?.name ?? null,
  lastActivityAt: c.updatedAt,
});

// One workspace's conversations, loaded once and kept here: the chat reads its record on every
// change, so the server is written to, not read from, after the first load.
type Cache = {
  key: string;
  rows: Map<string, Conversation>;
  saved: Set<string>;
  // Others' shared conversations whose messages are not read yet: the shared list carries none,
  // so a shared conversation is read whole the first time it is asked for.
  unread: Set<string>;
  // Projects others shared with the workspace, as listed with their conversations (the projects
  // repo reads them from here).
  sharedProjects: Record<string, unknown>[];
  ready: Promise<void>;
};
let cache: Cache | null = null;

const load = async (): Promise<Cache | null> => {
  const ctx = repoContext();
  if (!ctx) return null;
  const key = `${ctx.userId}:${ctx.workspaceId}`;
  if (cache?.key !== key) {
    const next: Cache = {
      key,
      rows: new Map(),
      saved: new Set(),
      unread: new Set(),
      sharedProjects: [],
      ready: Promise.resolve(),
    };
    next.ready = (async () => {
      const [rows, shared] = await Promise.all([
        InsightConversationStore.listForUser(ctx.userId, ctx.workspaceId),
        // What others shared is a bonus: the user's own list never waits on it failing.
        InsightConversationStore.listShared(ctx.workspaceId).catch((e) => {
          logger.error({ label: 'app.insights.conversationsRepo.listShared.error', message: e });
          return { conversations: [], projects: [] };
        }),
      ]);
      rows.forEach((row) => {
        next.rows.set(row.id, fromRow(row));
        next.saved.add(row.id);
      });
      shared.conversations.forEach((row) => {
        if (next.rows.has(row.id)) return;
        next.rows.set(row.id, { ...fromRow(row), readOnly: true });
        next.unread.add(row.id);
      });
      next.sharedProjects = shared.projects;
    })();
    cache = next;
    try {
      await next.ready;
    } catch (e) {
      if (cache === next) cache = null;
      throw e;
    }
    return next;
  }
  await cache.ready;
  return cache;
};

/** Projects others shared with this workspace, as loaded with the conversations. */
export const sharedProjectRows = async (): Promise<Record<string, unknown>[]> => {
  const c = await load();
  return c ? c.sharedProjects : [];
};

/** Forget the loaded list (another browser may have changed it): the next read reloads. */
export const reloadConversations = () => {
  cache = null;
};

// Writes to one conversation run one after another, so an update never reaches the server
// before the create it follows.
const queues = new Map<string, Promise<unknown>>();
const enqueue = <T>(id: string, run: () => Promise<T>): Promise<T> => {
  const next = (queues.get(id) ?? Promise.resolve()).catch(() => undefined).then(run);
  queues.set(id, next);
  return next;
};

const save = (c: Cache, conversation: Conversation): Promise<unknown> => {
  const ctx = repoContext();
  return enqueue(conversation.id, async () => {
    if (c.saved.has(conversation.id)) {
      await InsightConversationStore.update(conversation.id, fieldsOf(conversation));
      return;
    }
    if (!ctx) throw new Error('Not signed in to a workspace');
    await InsightConversationStore.create(
      conversation.id,
      ctx.userId,
      ctx.workspaceId,
      fieldsOf(conversation),
    );
    c.saved.add(conversation.id);
  });
};

const patch = (
  c: Cache,
  id: string,
  fields: Parameters<typeof InsightConversationStore.update>[1],
) => enqueue(id, () => InsightConversationStore.update(id, fields));

const sorted = (rows: Iterable<Conversation>) =>
  [...rows].sort((a, b) => b.updatedAt - a.updatedAt);

// DynamoDB-backed (InsightConversation, owner-only), per user and workspace.
export const conversationsRepo: ConversationsRepo = {
  async list() {
    const c = await load();
    return c ? sorted(c.rows.values()) : [];
  },
  async get(id) {
    const c = await load();
    if (c?.unread.has(id)) {
      const ctx = repoContext();
      if (ctx) {
        // On failure the list's entry (no messages) is returned and left unread, so the next
        // open tries again; none of the callers expects get() to reject.
        try {
          const row = await InsightConversationStore.getShared(id, ctx.workspaceId);
          c.rows.set(id, { ...fromRow(row), readOnly: true });
          c.unread.delete(id);
        } catch (e) {
          logger.error({ label: 'app.insights.conversationsRepo.getShared.error', message: e });
        }
      }
    }
    return c?.rows.get(id);
  },
  async create(title, messages, projectId, createdBy, originKey, sessionId) {
    const c = await load();
    if (!c) throw new Error('Not signed in to a workspace');
    const row: Conversation = {
      id: uid(),
      title,
      messages,
      updatedAt: Date.now(),
      projectId,
      createdBy,
      originKey,
      sessionId,
    };
    // In the list at once, so a read before the server answers finds it (and does not mint another).
    c.rows.set(row.id, row);
    await save(c, row);
    return row;
  },
  async rename(id, title) {
    const c = await load();
    const row = c?.rows.get(id);
    if (!c || !row || row.readOnly) return;
    c.rows.set(id, { ...row, title, updatedAt: Date.now() });
    await patch(c, id, { title, lastActivityAt: Date.now() });
  },
  async remove(id) {
    const c = await load();
    if (!c || c.rows.get(id)?.readOnly) return;
    c.rows.delete(id);
    if (c.saved.has(id)) await enqueue(id, () => InsightConversationStore.remove(id));
    c.saved.delete(id);
  },
  async share(id) {
    // Not a content change: the date is left alone, so sharing does not reshuffle the history.
    const c = await load();
    const row = c?.rows.get(id);
    if (!c || !row || row.readOnly) return;
    c.rows.set(id, { ...row, shared: true });
    await patch(c, id, { shared: true });
  },
  async unshare(id) {
    const c = await load();
    const row = c?.rows.get(id);
    if (!c || !row || row.readOnly) return;
    c.rows.set(id, { ...row, shared: false });
    await patch(c, id, { shared: false });
  },
  async listByProject(projectId) {
    return (await this.list()).filter((conversation) => conversation.projectId === projectId);
  },
  async listByOrigin(originKey) {
    return (await this.list()).filter((conversation) => conversation.originKey === originKey);
  },
  async setProject(id, projectId) {
    const c = await load();
    const row = c?.rows.get(id);
    if (!c || !row || row.readOnly) return;
    c.rows.set(id, { ...row, projectId: projectId ?? undefined });
    await patch(c, id, { projectId });
  },
  async upsert(conversation) {
    const c = await load();
    if (!c) throw new Error('Not signed in to a workspace');
    const prev = c.rows.get(conversation.id);
    if (prev?.readOnly) {
      // Someone else's shared conversation: reopening it changes nothing; continuing it saves the
      // whole thread, as the reader's own, under a new id (the caller follows the returned id).
      if (prev.messages.length === conversation.messages.length) return prev;
      const ctx = repoContext();
      const fork: Conversation = {
        ...conversation,
        id: uid(),
        readOnly: false,
        shared: false,
        createdBy: ctx ? { id: ctx.userId, name: ctx.userName ?? '' } : conversation.createdBy,
        updatedAt: Date.now(),
      };
      c.rows.set(fork.id, fork);
      save(c, fork).catch((e) =>
        logger.error({ label: 'app.insights.conversationsRepo.fork.error', message: e }),
      );
      return fork;
    }
    // Only advance the change date when a new turn was added. A turn appends messages, so the
    // count grows; resuming re-writes the SAME count. Comparing counts (not deep content) keeps
    // render-layer normalization noise from registering as a change and reshuffling the history.
    const contentChanged = !prev || prev.messages.length !== conversation.messages.length;
    const updated: Conversation = {
      ...conversation,
      updatedAt: contentChanged ? Date.now() : prev?.updatedAt ?? Date.now(),
    };
    c.rows.set(updated.id, updated);
    save(c, updated).catch((e) =>
      logger.error({ label: 'app.insights.conversationsRepo.upsert.error', message: e }),
    );
    return updated;
  },
};
